// Pure decision logic for crash-attribution (unclean-exit) detection.
//
// Why per-session keys: reliability.ts used to track ONE shared localStorage key
// ('har_reliability_open_session') across all tabs. Any reload while a SECOND tab
// was open found the sibling's still-open record and logged a false
// `session_recovered_after_unclean_exit` — poisoning every per-route crash
// histogram. The fix stores one key per session ('har_reliability_open_session:<id>')
// and, on init, sweeps them: a record is reported as unclean ONLY when it was never
// closed (closedAt == null) AND its heartbeat is stale (> 90s old). Live sibling
// tabs re-persist their heartbeat every 30s, so they stay under the threshold and
// are kept, not reported. See docs/REGRESSIONS.md (2026-07-01).
//
// Tradeoff: a crashed tab reloaded within 90s is not reported on that immediate
// load — its key ages past the threshold and is swept (with the crashed session's
// route/heartbeat preserved in the payload) on a later init.
//
// Kept pure (no localStorage / Date.now access) so vitest can cover the decisions.

/** Per-session key prefix; the full key is `${PREFIX}${sessionId}`. */
export const OPEN_SESSION_KEY_PREFIX = 'har_reliability_open_session:'

/** Retired single shared key — still swept so pre-migration records get cleaned up. */
export const LEGACY_OPEN_SESSION_KEY = 'har_reliability_open_session'

/** Heartbeats refresh every 30s; anything >90s stale is a dead session, not a live sibling. */
export const UNCLEAN_HEARTBEAT_STALE_MS = 90_000

export type OpenSessionRecord = {
  sessionId?: string
  heartbeatAt?: string
  closedAt?: string | null
  route?: string
  activeView?: string
  href?: string
  visibility?: string
}

/** Parse a persisted open-session record; null for missing/invalid/non-object payloads. */
export function parseOpenSessionRecord(raw: string | null | undefined): OpenSessionRecord | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    return parsed as OpenSessionRecord
  } catch {
    return null
  }
}

function heartbeatAgeMs(record: OpenSessionRecord, nowIso: string): number | null {
  if (typeof record.heartbeatAt !== 'string') return null
  const hb = Date.parse(record.heartbeatAt)
  const now = Date.parse(nowIso)
  if (Number.isNaN(hb) || Number.isNaN(now)) return null
  return now - hb
}

/**
 * True when a swept record should be REPORTED as an unclean exit:
 * open (closedAt == null) AND heartbeat stale (> UNCLEAN_HEARTBEAT_STALE_MS, or
 * missing/invalid — a live tab re-writes a valid heartbeat every 30s).
 */
export function shouldReportUncleanExit(record: OpenSessionRecord | null | undefined, nowIso: string): boolean {
  if (!record) return false
  if (record.closedAt) return false
  const age = heartbeatAgeMs(record, nowIso)
  if (age == null) return true
  return age > UNCLEAN_HEARTBEAT_STALE_MS
}

/**
 * True when a swept key must be KEPT (not deleted): the record is open with a
 * FRESH heartbeat — i.e. most likely a live sibling tab that will close its own
 * key on pagehide. Closed, invalid, or stale records return false (deletable).
 */
export function shouldKeepOpenSessionKey(record: OpenSessionRecord | null | undefined, nowIso: string): boolean {
  if (!record) return false
  if (record.closedAt) return false
  const age = heartbeatAgeMs(record, nowIso)
  if (age == null) return false
  return age <= UNCLEAN_HEARTBEAT_STALE_MS
}
